<script setup lang="ts" generic="T extends string | number">
import { ElDropdown, ElDropdownItem, ElDropdownMenu, ElIcon } from 'element-plus'
import { computed, ref } from 'vue'

import { BoxComponent, ButtonComponent, FlexComponent, TypographyComponent } from '../../'
import MoreIcon from '../../../assets/icons/more.svg?component'
import { useFitItemsToContainer } from '../../../hooks/useFitItemsToContainer'

import type { SegmentedFilterItem } from './SegmentedFilter.types'

const props = withDefaults(
  defineProps<{
    items: SegmentedFilterItem<T>[]
    max?: number
    allLabel?: string
  }>(),
  {
    max: 3,
    allLabel: 'All',
  },
)

const selected = defineModel<T | undefined>()

const containerRef = ref<InstanceType<typeof FlexComponent> | null>(null)

const { visibleCount } = useFitItemsToContainer(() => (containerRef.value?.$el as HTMLElement | null) ?? null, {
  max: () => props.max,
  itemCount: () => props.items.length,
  isClipped: (el) => el.scrollWidth > el.clientWidth,
})

const visibleItems = computed(() => props.items.slice(0, visibleCount.value))
const overflowItems = computed(() => props.items.slice(visibleCount.value))

const isOverflowActive = computed(() => overflowItems.value.some((item) => item.value === selected.value))

const handleSelect = (item: SegmentedFilterItem<T>) => {
  selected.value = selected.value === item.value ? undefined : item.value
}

const handleAllClick = () => {
  if (props.items.length === 0) {
    return
  }
  selected.value = undefined
}
</script>
<template>
  <FlexComponent ref="containerRef" :gap="0" wrap="nowrap" class="segmented-filter" :class="{ 'is-empty': items.length === 0 }">
    <span class="segmented-filter__indicator" aria-hidden="true" />
    <ButtonComponent
      variant="text"
      class="segmented-filter__button segmented-filter__button--all"
      :class="{
        'is-active': selected === undefined && items.length > 0,
        'is-static': items.length === 0,
      }"
      @click="handleAllClick"
    >
      <TypographyComponent variant="label-large" :font-weight="600">
        {{ allLabel }}
      </TypographyComponent>
    </ButtonComponent>
    <ButtonComponent
      v-for="item in visibleItems"
      :key="item.value"
      variant="text"
      class="segmented-filter__button"
      :class="{
        'segmented-filter__button--no-icon': !item.icon,
        'is-active': selected === item.value,
      }"
      @click="handleSelect(item)"
    >
      <ElIcon v-if="item.icon" :size="14" color="var(--primary)">
        <component :is="item.icon" class="segmented-filter__icon" />
      </ElIcon>
      <TypographyComponent variant="label-large" :font-weight="600">
        {{ item.label }}
      </TypographyComponent>
    </ButtonComponent>
    <ElDropdown v-if="overflowItems.length > 0" trigger="click" @command="handleSelect">
      <ButtonComponent
        variant="text"
        class="segmented-filter__button segmented-filter__button--overflow"
        :class="{ 'is-active': isOverflowActive }"
      >
        <ElIcon :size="18" color="var(--primary)">
          <MoreIcon />
        </ElIcon>
      </ButtonComponent>
      <template #dropdown>
        <BoxComponent :w="200">
          <ElDropdownMenu>
            <ElDropdownItem v-for="item in overflowItems" :key="item.value" :command="item">
              <BoxComponent
                :p="16"
                w="100%"
                class="segmented-filter__dropdown-item"
                :class="{ 'is-active': selected === item.value }"
                :title="item.label"
              >
                <ElIcon v-if="item.icon" :size="14" color="var(--primary)">
                  <component :is="item.icon" />
                </ElIcon>
                <TypographyComponent variant="body-large" text-truncate>
                  {{ item.label }}
                </TypographyComponent>
              </BoxComponent>
            </ElDropdownItem>
          </ElDropdownMenu>
        </BoxComponent>
      </template>
    </ElDropdown>
  </FlexComponent>
</template>
<style lang="scss" scoped>
.segmented-filter {
  position: relative;
  align-self: flex-start;
  height: 40px;
  padding: 4px;
  border-radius: 40px;
  border: 1px solid var(--button-hover);
  overflow: hidden;
  max-width: 100%;
  min-width: 108px;

  &.is-empty {
    padding: 0;
    border: none;
    min-width: 0;

    .segmented-filter__button--all {
      padding: 0;

      &:hover {
        background: transparent;
      }
    }
  }
}
.segmented-filter__indicator {
  display: none;
}
.button.segmented-filter__button {
  position: relative;
  height: 30px;
  min-height: 30px;
  padding: 0 16px 0 12px;
  gap: 4px;
  flex: 0 0 auto;
  opacity: 0.32;
  transition: opacity 120ms ease;

  &--all,
  &--no-icon {
    padding: 0 16px;
  }

  &--overflow {
    padding: 0 16px;
    opacity: 1;
  }

  &.is-active {
    opacity: 1;
    background: var(--inverted);
    box-shadow: 0 1px 6px 0 rgba(0, 0, 0, 0.16);
    transition: opacity 120ms ease 160ms;
  }

  &.is-static {
    cursor: default;
    opacity: 1;
  }

  :deep(.el-icon) {
    path {
      fill: currentColor;
    }
  }
}

@supports (anchor-name: --x) {
  .segmented-filter__button.is-active {
    anchor-name: --segmented-filter-active;
    background: transparent;
    box-shadow: none;
  }
  .segmented-filter__indicator {
    display: block;
    position: absolute;
    position-anchor: --segmented-filter-active;
    top: anchor(top);
    left: anchor(left);
    width: anchor-size(width);
    height: anchor-size(height);
    background: var(--inverted);
    border-radius: 40px;
    box-shadow: 0 1px 6px 0 rgba(0, 0, 0, 0.16);
    pointer-events: none;
    transition:
      top 240ms cubic-bezier(0.4, 0, 0.2, 1),
      left 240ms cubic-bezier(0.4, 0, 0.2, 1),
      width 240ms cubic-bezier(0.4, 0, 0.2, 1),
      height 240ms cubic-bezier(0.4, 0, 0.2, 1);
  }
}

:deep(.el-dropdown-menu__item) {
  padding: 0;
  white-space: normal;
}
</style>
<style lang="scss">
.segmented-filter__dropdown-item {
  display: flex;
  align-items: center;
  gap: 8px;

  &.is-active {
    font-weight: 600;
    background: var(--button-hover);
  }

  .el-icon {
    path {
      fill: currentColor;
    }
  }
}
</style>
